import React from 'react'
import { useAppSelector } from 'app/hooks'
import {
    AdminView,
    RootState,
    NestedDict,
    FieldType,
    ValidateType,
    LocationType,
} from 'app/types'
import { LocationFields, LocationDefault, nudgeDefaults } from 'app/constants'
import { validateApp } from 'helpers/authHeader'
import { AdminEditor } from 'views/AdminPage/AdminEditor'
import * as fnc from 'helpers/fnc'

export const defaultProjectMetadata = {
    id: null,
    name: '',
    link: '',
    domain: null,
    registerLink: '',
    title: null,
    subtitle: null,
    summary: '',
    statusUpdate: '',
    description: '',
    altAppLink: '',
    builderId: null,
    projectStatusId: null,
    projectTypes: [],
    organizations: [],
    colorSchemeId: null,
    themeId: null,
    locationId: null,
    officeLocationId: null,
    published: false,
    demo: false,
    estCompletion: null,
    bookingLaunch: null,
    bookingEmail: null,
    bookingLink: null,
    bookingPhone: null,
    termsAndConditions: null,
    pricing: null,
    locations: [],
    agents: [],
    maintenanceMode: false,
    enableAvailability: false,
    enableFinancial: false,
    enableWorksheets: false,
    enableSharing: false,
    enableAnalytics: false,
    enableSales: false,
    enableNewsletter: false,
    enableUpgrades: false,
    mediaId: null,
    logoMediaId: null,
    logoNotextMediaId: null,
    logoTopbarMediaId: null,
    logoSidebarMediaId: null,
    faviconMediaId: null,
    hotjarId: null,
    facebookPixelId: null,
    googleTagId: null,
    googleTagManagerId: null,
    hubspotAccessToken: null,
    hubspotClientSecret: null,
    hubspotTrackingId: null,
    hubspotContactDefaults: null,
    registrationForwardConfig: null,
    showPricing: false,
    showPricingSalescenter: false,
    enableLoginSalescenter: false,
    requireLoginSalescenter: false,
    enableRequestInfo: true,
    nudgeTitle: 'Register Today – Stay Connected!',
    nudgeMessage: 'Register to get exclusive updates, project news, and the latest events!',
    nudgeTimeout: 300000,
    nudgeSecondaryTimeout: 1800000,
    nudgeEnable: true,
    requestInfoConfig: null,
    brokerPortalPassword: null,
    salesPercent: null,
    floorplanFeaturesTitle: null,
    dateCreated: null,
}

interface ProjectMetadataEditorProps {
    app?: any
    delta: NestedDict
    onChange: () => void
    onSubmit: () => void
    checkChange: () => void
    mode?: 'full' | 'project-only' // 'full' for AppsView, 'project-only' for GeneralView
}

export function ProjectMetadataEditor(props: ProjectMetadataEditorProps) {
    const {
        app,
        delta,
        onChange,
        onSubmit,
        checkChange,
        mode = 'full'
    } = props

    const builders = useAppSelector((state: RootState) => state.admin.builders)
    const config = useAppSelector((state: RootState) => state.app.config)
    const { themes, colorSchemes, projectTypes, projectStatuses } = config

    let organizations = useAppSelector((state: RootState) => state.admin.organizations)
    if (organizations == null) {
        organizations = config.organizations
    }

    // Handle change events - need to call parent onChange with the change data
    const handleChange = (editData) => {
        if (onChange && mode === 'project-only') {
            // For project-only mode, only include fields that have actually been modified
            // We need to be more careful about what constitutes a "change"
            const changes = {}
            
            // Compare each field to detect ACTUAL changes (not just different from defaults)
            Object.keys(editData).forEach(key => {
                const originalValue = app?.meta?.[key] ?? app?.[key]
                const newValue = editData[key]
                
                // Only include in changes if:
                // 1. The value is actually different from the original
                // 2. AND it's not just a null/undefined conversion (which isn't a real change)
                if (JSON.stringify(originalValue) !== JSON.stringify(newValue)) {
                    // Don't include changes where we're just converting undefined to null
                    if (!(originalValue === undefined && newValue === null)) {
                        // Don't include changes where we're converting null to empty string or vice versa
                        if (!(
                            (originalValue === null && newValue === '') || 
                            (originalValue === '' && newValue === null) ||
                            (originalValue === undefined && newValue === '')
                        )) {
                            changes[key] = newValue
                        }
                    }
                }
            })
            
            // Merge with existing delta to accumulate changes, but only real changes
            const accumulatedChanges = {
                ...delta, // Keep existing changes
                ...changes // Add new changes
            }
            
            // Remove any fields that have been reverted to original values
            Object.keys(accumulatedChanges).forEach(key => {
                const originalValue = app?.meta?.[key] ?? app?.[key]
                const currentValue = editData[key]
                
                // If current value matches original, remove from accumulated changes
                if (JSON.stringify(originalValue) === JSON.stringify(currentValue)) {
                    delete accumulatedChanges[key]
                }
                
                // Also remove if it's a meaningless null/undefined/empty conversion
                if (
                    (originalValue === undefined && currentValue === null) ||
                    (originalValue === null && currentValue === '') ||
                    (originalValue === undefined && currentValue === '') ||
                    (originalValue === '' && currentValue === null)
                ) {
                    delete accumulatedChanges[key]
                }
            })
            
            // Only call onChange if there are meaningful changes
            if (Object.keys(accumulatedChanges).length > 0) {
                onChange(accumulatedChanges)
            } else {
                onChange(null) // Reset delta if no meaningful changes
            }
        } else if (onChange) {
            // For full mode, pass through the editData as-is
            onChange(editData)
        }
    }

    // Handle submit events
    const handleSubmit = () => {
        if (onSubmit) {
            if (mode === 'project-only') {
                // For project-only mode, pass the delta object to onSubmit
                return onSubmit(delta)
            } else {
                // For full mode, pass delta as before
                return onSubmit(delta)
            }
        }
        return Promise.resolve(true)
    }

    // Define all metadata fields (available in both modes)
    const allMetadataFields = {
        name: {
            type: FieldType.String,
        },
        link: {
            type: FieldType.String,
        },
        domain: {
            type: FieldType.String,
            list: false,
        },
        title: {
            label: 'Title',
            type: FieldType.String,
            list: false,
        },
        subtitle: {
            label: 'Subtitle',
            type: FieldType.String,
            list: false,
        },
        description: {
            label: 'Description (social)',
            type: FieldType.String,
            list: false,
        },
        estCompletion: {
            label: 'Estimated Completion Date',
            type: FieldType.String,
            list: false,
        },
        bookingLaunch: {
            label: 'Booking Launch Date',
            type: FieldType.String,
            list: false,
        },
        bookingEmail: {
            label: 'Booking Email',
            type: FieldType.String,
            list: false,
        },
        bookingLink: {
            label: 'Booking Link',
            type: FieldType.String,
            list: false,
        },
        bookingPhone: {
            label: 'Booking Phone',
            type: FieldType.String,
            validation: ValidateType.Phone,
            list: false,
        },
        pricing: {
            label: 'Pricing',
            type: FieldType.String,
            list: false,
        },
        builderId: {
            label: 'Builder',
            type: FieldType.Option,
            options: builders ? [{ id: null, name: 'None' }, ...builders] : [],
        },
        projectStatusId: {
            label: 'Project Status',
            type: FieldType.Option,
            list: false,
            options: projectStatuses ? [{ id: null, name: 'None' }, ...Object.values(projectStatuses)] : [],
        },
        projectTypes: {
            label: 'Project Type',
            type: FieldType.List,
            list: false,
            selector: (x) => x.id,
            options: projectTypes ? Object.values(projectTypes) : [],
        },
        locationId: {
            type: FieldType.Location,
            label: 'Project Location',
            addLabel: 'Location',
            locationTypeId: LocationType.Primary,
            list: false,
        },
        officeLocationId: {
            type: FieldType.Location,
            label: 'Office Location',
            addLabel: 'Location',
            locationTypeId: LocationType.Primary,
            list: false,
        },
        mediaId: {
            label: 'Media',
            type: FieldType.Media,
        },
        logoMediaId: {
            label: 'Logo',
            type: FieldType.Media,
        },
        logoNotextMediaId: {
            label: 'Logo No Text',
            type: FieldType.Media,
            list: false,
        },
        logoTopbarMediaId: {
            label: 'Logo Topbar',
            type: FieldType.Media,
            list: false,
        },
        logoSidebarMediaId: {
            label: 'Logo Sidebar',
            type: FieldType.Media,
            list: false,
        },
        faviconMediaId: {
            label: 'Favicon',
            type: FieldType.Media,
            list: false,
        },
        altAppLink: {
            label: 'Alt Link',
            type: FieldType.String,
            list: false,
        },
        registerLink: {
            label: 'Register Link',
            type: FieldType.String,
            list: false,
        },
        termsAndConditions: {
            label: 'Terms and Conditions',
            type: FieldType.String,
            list: false,
        },
        hotjarId: {
            label: 'Hotjar Id',
            type: FieldType.String,
            list: false,
        },
        facebookPixelId: {
            label: 'Facebook Pixel Id',
            type: FieldType.String,
            list: false,
        },
        googleTagId: {
            label: 'Google Analytics Id',
            type: FieldType.String,
            list: false,
        },
        googleTagManagerId: {
            label: 'Google Tag Manager Id',
            type: FieldType.String,
            list: false,
        },
        hubspotAccessToken: {
            label: 'Hubspot Access Token',
            type: FieldType.String,
            list: false,
            adminOnly: true,
        },
        hubspotClientSecret: {
            label: 'Hubspot Client Secret',
            type: FieldType.String,
            list: false,
            adminOnly: true,
        },
        hubspotTrackingId: {
            label: 'Hubspot Tracking Id',
            type: FieldType.String,
            list: false
        },
        hubspotContactDefaults: {
            label: 'Hubspot Contact Defaults',
            type: FieldType.String,
            list: false,
        },
        registrationForwardConfig: {
            label: 'Registration Forward Config',
            type: FieldType.String,
            list: false,
        },
        colorSchemeId: {
            label: 'Color Scheme',
            type: FieldType.Option,
            list: false,
            options: colorSchemes ? [{ id: null, name: 'None' }, ...colorSchemes] : [],
            optionMap: (x) => {
                const colors = [x.primaryColor, x.secondaryColor, x.tertiaryColor, x.backgroundColor]
                const schemeSummary = colors.map((x) => <div className="color-swatch" style={{ backgroundColor: `#${x}` }} />)
                return {
                    text: <React.Fragment>{x.name} <div className="swatches">{schemeSummary}</div></React.Fragment>,
                    value: x.id,
                }
            },
        },
        themeId: {
            label: 'Theme',
            type: FieldType.Option,
            list: false,
            options: themes ? [{ id: null, name: 'None' }, ...themes] : [],
        },
        agents: {
            label: 'Agents',
            type: FieldType.List,
            list: false,
            selector: (x) => x.id,
            options: (editData) => {
                const agentSet = {}
                editData.organizations.forEach((x) => {
                    const org = organizations.find((y) => y.id == x)
                    if (org) {
                        org.agents.forEach((y) => {
                            if (!(y.id in agentSet)) {
                                agentSet[y.id] = y
                            }
                        })
                    }
                })
                return Object.values(agentSet)
            },
            optionMap: (x) => {
                return {
                    text: <React.Fragment>{x.name} / {fnc.phoneNumber(x.phone)} / {x.email}</React.Fragment>,
                    value: x.id,
                }
            },
            disable: (editData) => {
                if (editData.organizations.length == 0) {
                    return 'Agents can only be assigned to apps within an organization'
                }
                return false
            },
        },
        published: {
            header: 'Configuration',
            type: FieldType.Toggle,
            text: 'Appear in Organization',
        },
        demo: {
            type: FieldType.Toggle,
            text: 'Demo Instance',
            list: false,
        },
        showPricing: {
            label: 'Show Floorplan Pricing Online',
            type: FieldType.Toggle,
            list: false,
        },
        showPricingSalescenter: {
            label: 'Show Floorplan Pricing at Sales Center',
            type: FieldType.Toggle,
            list: false,
        },
        requireLoginSalescenter: {
            label: 'Require Login at Sales Center',
            type: FieldType.Toggle,
            list: false,
        },
        enableLoginSalescenter: {
            label: 'Enable Login at Sales Center',
            type: FieldType.Toggle,
            list: false,
        },
        enableAvailability: {
            label: 'Enable Availability',
            type: FieldType.Toggle,
            list: false,
        },
        enableFinancial: {
            label: 'Enable Financial',
            type: FieldType.Toggle,
            list: false,
        },
        enableWorksheets: {
            label: 'Enable Worksheets',
            type: FieldType.Toggle,
            list: false,
        },
        enableSharing: {
            label: 'Enable Sharing',
            type: FieldType.Toggle,
            list: false,
        },
        enableAnalytics: {
            label: 'Enable Analytics',
            type: FieldType.Toggle,
            list: false,
        },
        enableSales: {
            label: 'Enable Sales',
            type: FieldType.Toggle,
            list: false,
        },
        enableNewsletter: {
            label: 'Enable Newsletter',
            type: FieldType.Toggle,
            list: false,
        },
        enableUpgrades: {
            label: 'Enable Upgrades',
            type: FieldType.Toggle,
            list: false,
        },
        enableRequestInfo: {
            label: 'Enable Request Info',
            type: FieldType.Toggle,
            list: false,
        },
        requestInfoConfig: {
            label: 'Request Info Config',
            type: FieldType.String,
            list: false,
        },
        brokerPortalPassword: {
            label: 'Broker Portal Password',
            type: FieldType.String,
            list: false,
        },
        salesPercent: {
            label: 'Sales Percent (%)',
            type: FieldType.Float,
            list: false,
        },
        floorplanFeaturesTitle: {
            label: 'Floorplan Features Title',
            type: FieldType.String,
            list: false,
        },
        nudgeEnable: {
            label: 'Enable Registration Nudge',
            type: FieldType.Toggle,
            list: false,
        },
        nudgeTitle: {
            header: 'Registration Nudge',
            label: 'Nudge Title',
            placeholder: nudgeDefaults.title,
            type: FieldType.String,
            textarea: true,
            list: false,
        },
        nudgeMessage: {
            label: 'Nudge Message',
            type: FieldType.String,
            placeholder: nudgeDefaults.message,
            textarea: true,
            list: false,
        },
        nudgeTimeout: {
            label: 'Primary Nudge Timeout (ms)',
            type: FieldType.Integer,
            list: false,
        },
        nudgeSecondaryTimeout: {
            label: 'Secondary Nudge Timeout (ms)',
            type: FieldType.Integer,
            list: false,
        },
        maintenanceMode: {
            header: 'Critical',
            label: 'Maintenance Mode',
            type: FieldType.Toggle,
            list: false,
        },
        dateCreated: {
            type: FieldType.Date,
            label: 'Date Created',
            edit: false,
        },
    }

    // Use all fields for both modes - the distinction is in data handling, not field visibility
    const fields = allMetadataFields

    // For project-only mode, we need to use the current app as the data source
    const targetView = AdminView.AllProjects
    
    // Use app metadata directly for project-only mode, merged with delta
    const editData = mode === 'project-only' && app ? {
        ...defaultProjectMetadata, // Start with defaults
        ...app.meta, // Add app metadata
        ...app, // Include top-level app properties
        ...delta, // Apply accumulated changes from delta - this is the key fix
        id: app.meta?.id || app.id,
        name: app.meta?.name || app.name,
        link: app.meta?.link || app.link,
    } : undefined

    return <AdminEditor
        view={targetView}
        validation={validateApp}
        checkChange={checkChange}
        onChange={handleChange}
        onSubmit={handleSubmit}
        fields={fields}
        newData={mode === 'full' ? defaultProjectMetadata : undefined}
        editData={editData}
        compactMode={mode === 'project-only'}
    />
}